import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { TextField, Typography } from "@mui/material";
import FormFieldContainer, {
  FormContainer,
  inputStyles,
} from "components/ui/FormField/FormFieldContainer";
import Page from "components/ui/Page/Page";
import { loginAPI, userProfile, verifyEmail } from "data";
import { useEnrollmentForm } from "hooks/useEnrollmentForm";

const Login = ({ email, onLoginSuccess, onCancel }) => {
  const params = useParams();
  const SPONSOR_ID = params.sponsor.toUpperCase();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState(null); // For storing any error messages if needed in the future

  const { formData, handleFormChange, onSubmit, isSubmitting } =
    useEnrollmentForm({
      login: {
        email: email || "",
        password: "",
      },
    });

  const handleChange = (event) => {
    setLoginError(null);
    const { name, value } = event.target;
    handleFormChange("login", name, value);
  };

  const handleSubmit = () => {
    onSubmit(async () => {
      try {
        setLoginError(null);
        // Step 1: Login API call
        const loginResponse = await loginAPI({
          email: formData.login.email?.trim(),
          password: formData.login.password,
          sponsorSymbol: SPONSOR_ID,
        });
        const loginData = await loginResponse.json();
        if (!loginResponse.ok) {
          throw new Error(loginData?.errors?.[0]?.title || "Login failed");
        }

        // Step 2: Verify Email
        const verifyEmailPromise = await verifyEmail({
          email: formData.login.email?.trim(),
        });
        const verifyEmailData = await verifyEmailPromise.json();
        if (!verifyEmailPromise.ok || verifyEmailData?.errors) {
          throw new Error(
            verifyEmailData?.errors?.[0]?.title || "Email verification failed",
          );
        }

        if (verifyEmailData?.isKCCUser) {
          // Step 3: Fetch User Profile
          const userProgressPromise = await userProfile(loginData?.auth_token);
          const userProgressData = await userProgressPromise.json();
          if (userProgressData?.[0]?.stepID == "ShippingAddress") {
            throw new Error("You've already completed KardiaComplete enrollment");
          } else if (!userProgressPromise.ok) {
            throw new Error(
              userProgressData?.errors?.[0]?.title ||
              "User profile retrieval failed",
            );
          }
        }

        onLoginSuccess(loginData);
      } catch (err) {
        setLoginError(err.message);
        console.log(err);
      }
    });
  };

  const isSubmitDisabled =
    !formData.login.email || !formData.login.password || isSubmitting;

  return (
    <Page
      heading="Log In"
      subHeading="Already have an account? Enter your log-in information below to complete registration."
    >
      <Typography sx={{ color: "#475569", fontSize: "16px" }}>
        Are you new to KardiaComplete?{" "}
        <Link onClick={onCancel}>Create an Account</Link>.
      </Typography>

      <FormContainer
        sx={{
          width: "65%",
        }}
      >
        <FormFieldContainer
          heading="Email"
          required
          fields={[
            <TextField
              fullWidth
              name="email"
              placeholder="Email"
              value={formData.login.email}
              onChange={handleChange}
              disabled={isSubmitting}
              inputProps={{
                autocomplete: "new-password",
                form: {
                  autocomplete: "off",
                },
              }}
              sx={inputStyles()}
            />,
          ]}
        />

        <FormFieldContainer
          heading="Password"
          required
          fields={[
            <TextField
              fullWidth
              name="password"
              placeholder="Password"
              type="password"
              value={formData.login.password}
              disabled={isSubmitting}
              onChange={handleChange}
              inputProps={{
                autocomplete: "new-password",
                form: {
                  autocomplete: "off",
                },
              }}
              sx={inputStyles()}
            />,
          ]}
        />

        <Typography
          sx={{
            textAlign: "center",
            fontSize: "17px",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}
        >
          <Link
            to={`${process.env.REACT_APP_ALIVECOR_ENDPOINT}account/resend_password`}
            style={{ textDecoration: "none" }}
          >
            Forgot your password?
          </Link>
        </Typography>

        <LoadingButton
          variant="contained"
          loading={isSubmitting}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          Login
        </LoadingButton>
        {loginError && (
          <Typography sx={{ mt: 2, color: "error.main" }}>
            {loginError}
          </Typography>
        )}
      </FormContainer>
    </Page>
  );
};

export default Login;
